import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    X,
    Home,
    MapPin,
    MessageSquare,
    UserPlus,
    Grid,
    User,
    LogOut,
} from "lucide-react";
import { GiWorld } from "react-icons/gi";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RoleEnum } from "../model/role.enum";
import { LogoutManually, LogoutUser, useAppDispatch } from "./../../../redux";
import SocialMediaLinks from "../../../components/ui/SocialMediaLinks";

const MobileMenu2 = ({ isOpen, onClose, isAuthenticated = false, onLogout, currentUser }) => {
    const { t } = useTranslation();
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const navigate = useNavigate();
    const isProfessional = Number(currentUser?.role?.id) === RoleEnum.professional||Number(currentUser?.role?.id) === RoleEnum.internationl_professional;

    if (!isOpen) return null;

    // Function to truncate email if it's too long
    const truncateEmail = (email, maxLength = 20) => {
        if (!email) return "";
        if (email.length <= maxLength) return email;
        return email.substring(0, maxLength) + "...";
    };

    const handleMyAccountClick = () => {
        if (!isAuthenticated) {
            navigate("/auth/login");
        } else {
            handleAccountClick();
            // If authenticated, navigate directly to dashboard
            onClose();
        }
    };

    const handleContinueWithEmail = () => {
        window.location.href = "/register";
        setShowLoginPopup(false);
    };

    const handleLogin = () => {
        window.location.href = "/auth/login";
        setShowLoginPopup(false);
    };

    const dispatch = useAppDispatch();
    const handleLogout = async () => {
        try {
            await dispatch(LogoutUser({})).unwrap();
            // Navigation is handled in the LogoutUser action
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
                // Navigate after manual logout
                requestAnimationFrame(() => {
                    window.location.replace("/");
                });
            } else {
                console.log("Logout failed:", error);
            }
        }
    };
    const handleAccountClick = () => {
        if (isProfessional) {
            navigate("/professionals");
        } else if (Number(currentUser?.role?.id) === RoleEnum.client) {
            navigate("/clients");
        }
    };

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

            {/* Mobile Menu */}
            <div className="fixed left-0 top-0 w-[280px] sm:w-[320px] h-full bg-white z-50 overflow-y-auto border-r border-gray-200 shadow-lg">
                {/* Header */}
                <div className="sticky top-0 bg-white p-4 border-b border-gray-100">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-900">{t("mobileMenu.menu")}</h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                            <X className="h-5 w-5 text-gray-600" />
                        </button>
                    </div>
                    {isAuthenticated && currentUser && (
                        <div className="mt-3 flex items-center">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                                {(() => {
                                    const photoUrl = currentUser?.photo?.path;
                                    const altName = `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim();
                                    
                                    // Check if the photo URL has a valid image extension
                                    const isValidImage = photoUrl && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(photoUrl);
                                    
                                    return isValidImage ? (
                                        <img
                                            src={photoUrl}
                                            alt={altName || "User"}
                                            className="w-full h-full rounded-full object-cover"
                                        />
                                    ) : (
                                        <User className="h-5 w-5 text-blue-600" />
                                    );
                                })()}
                            </div>
                            <div className="ml-3">
                                <div className="font-medium text-sm">{`${currentUser.firstName || ""} ${currentUser.lastName || ""}`}</div>
                                <div className="text-xs text-gray-500" title={currentUser.email}>
                                    {truncateEmail(currentUser.email)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="p-4">
                    <nav className="space-y-6">
                        {/* Main Navigation */}
                        <div className="space-y-1">
                            <button
                                onClick={e => {
                                    e.preventDefault();
                                    handleMyAccountClick();
                                }}
                                className="w-full flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                                <Home className="h-5 w-5" />
                                <span className="font-medium">{t("mobileMenu.myAccount")}</span>
                            </button>

                            <Link
                                to={Number(currentUser?.role?.id) === RoleEnum.professional || Number(currentUser?.role?.id) === RoleEnum.internationl_professional ? "/professional" : "/"}
                                className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                                <MapPin className="h-5 w-5" />
                                <span className="font-medium">{t("mobileMenu.localServices")}</span>
                            </Link>

                            <a
                                href={(Number(currentUser?.role?.id) === RoleEnum.professional || Number(currentUser?.role?.id) === RoleEnum.internationl_professional) ? "/international-professionals" : "/internationalhompage"}
                                className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                                <GiWorld className="h-5 w-5" />
                                <span className="font-medium">{t("mobileMenu.internationalServices")}</span>
                            </a>

                            <a
                                href="/menu/contact"
                                className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                                <MessageSquare className="h-5 w-5" />
                                <span className="font-medium">{t("mobileMenu.contactUs")}</span>
                            </a>

                            {isAuthenticated && (
                                <a
                                    href={currentUser && Number(currentUser.role.id) === RoleEnum.client ? "client/MessageAndVideoCall?to=client/myoffers" : "/MessageAndVideoCall?to=myoffers"}
                                    className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                                    <MessageSquare className="h-5 w-5" />
                                    <span className="font-medium">{t("mobileMenu.messages")}</span>
                                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                        3
                                    </span>
                                </a>
                            )}

                            <a
                                href="/invite"
                                className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                                <UserPlus className="h-5 w-5" />
                                <span className="font-medium">{t("mobileMenu.inviteFriends")}</span>
                            </a>

                            <a
                                href="/categories"
                                className="flex items-center space-x-3 p-3 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                                <Grid className="h-5 w-5" />
                                <span className="font-medium">{t("mobileMenu.categoriesAndLocations")}</span>
                            </a>
                        </div>

                        {/* Auth Buttons */}
                        <div className="flex space-x-3 pt-4 border-t border-gray-100">
                            {isAuthenticated ? (
                                <div className="w-full flex gap-1 justify-between items-center">
                                    <button
                                        onClick={() => handleAccountClick()}
                                        className="w-[47%] flex-1 text-white border border-gray-200  text-center rounded-lg bg-blue-600 hover:bg-blue-700  py-2.5   transition-colors text-sm font-medium flex items-center justify-center">
                                        <User className="h-4 w-4 mr-1" />
                                        {t("mobileMenu.myAccount")}
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="w-[47%] flex-1 text-gray-700 border border-gray-200  text-center rounded-lg hover:bg-gray-50   py-2.5   transition-colors text-sm font-medium flex items-center justify-center">
                                        <LogOut className="h-4 w-4 mr-1" />
                                        {t("mobileMenu.logout")}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <Link
                                        to={"/auth/register"}
                                        className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-center rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                                        {t("mobileMenu.register")}
                                    </Link>
                                    <Link
                                        to={"/auth/login"}
                                        className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-center rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
                                        {t("mobileMenu.login")}
                                    </Link>
                                </>
                            )}
                        </div>

                        {/* App Store Buttons */}
                        <div className="grid grid-cols-2 gap-3 pt-4">
                            <a href="https://play.google.com/store/apps/details?id=com.kokobeo">
                                <img
                                    src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
                                    alt={t("mobileMenu.getOnGooglePlay")}
                                    className="w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
                                />
                            </a>
                            <a href="https://apps.apple.com/app/kokobeo">
                                <img
                                    src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
                                    alt={t("mobileMenu.downloadOnAppStore")}
                                    className="w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
                                />
                            </a>
                        </div>

                        {/* Social Media Icons */}
                        <SocialMediaLinks containerSize="w-9 h-9" />
                    </nav>
                </div>
            </div>

            {/* Login/Registration Popup - Only shown when not authenticated */}
            {!isAuthenticated && (
                <AnimatePresence>
                    {showLoginPopup && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
                            <motion.div
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.95 }}
                                className="bg-white rounded-[20px] max-w-[90%] sm:max-w-md w-full shadow-lg">
                                <div className="p-4 sm:p-6">
                                    <div className="flex items-center justify-between mb-2 sm:mb-4">
                                        <div className="flex items-center">
                                            <img
                                                src="https://assests.netlify.app/assets/images/logo.png"
                                                alt="Kokobeo"
                                                className="h-6 sm:h-8"
                                            />
                                            <span className="ml-2 font-semibold text-base sm:text-lg">Kokobeo</span>
                                        </div>
                                        <button
                                            onClick={() => setShowLoginPopup(false)}
                                            className="p-1 sm:p-2 hover:bg-gray-100 rounded-full bg-red-500 text-white">
                                            <X className="h-3 sm:h-5 w-3 sm:w-5" />
                                        </button>
                                    </div>
                                    <h3 className="text-base sm:text-xl font-semibold mb-2 sm:mb-4">
                                        {t("mobileMenu.createAccount")}
                                    </h3>
                                    <p className="text-gray-600 text-sm sm:text-base mb-2 sm:mb-4">{t("mobileMenu.iWantTo")}</p>
                                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
                                        <button
                                            onClick={() => console.log("Hire Professional clicked")}
                                            className="w-full sm:flex-1 bg-white border border-gray-300 rounded-lg p-2 sm:p-4 flex flex-col items-center hover:bg-gray-50">
                                            <User className="h-4 sm:h-6 w-4 sm:w-6 text-gray-500 mb-1 sm:mb-2" />
                                            <span className="text-xs sm:text-sm text-gray-700">{t("mobileMenu.hireProfessional")}</span>
                                        </button>
                                        <button
                                            onClick={() => console.log("Work as Professional clicked")}
                                            className="w-full sm:flex-1 bg-white border border-gray-300 rounded-lg p-2 sm:p-4 flex flex-col items-center hover:bg-gray-50">
                                            <User className="h-4 sm:h-6 w-4 sm:w-6 text-gray-500 mb-1 sm:mb-2" />
                                            <span className="text-xs sm:text-sm text-gray-700">
                                                {t("mobileMenu.workAsProfessional")}
                                            </span>
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-4">
                                        <button className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50">
                                            <img
                                                src="https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-09-512.png"
                                                alt={t("mobileMenu.google")}
                                                className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2"
                                            />
                                            {t("mobileMenu.google")}
                                        </button>
                                        <button className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50">
                                            <svg viewBox="0 0 384 512" width="16" height="16" className="mr-1 sm:mr-2">
                                                <path
                                                    fill="currentColor"
                                                    d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
                                                />
                                            </svg>
                                            {t("mobileMenu.apple")}
                                        </button>
                                        <button className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50">
                                            <img
                                                src="https://cdn2.iconfinder.com/data/icons/social-media-2210/24/Facebook-512.png"
                                                alt={t("mobileMenu.facebook")}
                                                className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2"
                                            />
                                            {t("mobileMenu.facebook")}
                                        </button>
                                        <button className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50">
                                            <img
                                                src="https://cdn3.iconfinder.com/data/icons/social-media-2210/24/Instagram-512.png"
                                                alt={t("mobileMenu.instagram")}
                                                className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2"
                                            />
                                            {t("mobileMenu.instagram")}
                                        </button>
                                    </div>
                                    <p className="text-gray-500 text-xs sm:text-sm mb-2 sm:mb-4">{t("mobileMenu.or")}</p>
                                    <button
                                        onClick={handleContinueWithEmail}
                                        className="w-full bg-blue-600 text-white py-1.5 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm">
                                        {t("mobileMenu.continueWithEmail")}
                                    </button>
                                    <p className="text-sm text-gray-600 mt-2 text-center text-xs sm:text-sm">
                                        {t("mobileMenu.alreadyHaveAccount")}
                                        <button
                                            onClick={handleLogin}
                                            className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm ml-1">
                                            {t("mobileMenu.logIn")}
                                        </button>
                                    </p>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </>
    );
};

export default MobileMenu2;
